import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card, Badge, Skeleton } from "@hotelise/ui";

import { CurrencyAmount, Property } from "models/properties";
import { Product } from "interfaces/products";
import { getPropertyDetailsInfo } from "state/modules/properties";
import ProductsClient from "services/api/products";

const PropertySettings = (): JSX.Element => {
	const [welcomePackProduct, setWelcomePackProduct] = useState<Product | null>(
		null
	);
	const [isLoading, setIsLoading] = useState(false);

	const propertyDetails = useSelector(getPropertyDetailsInfo) as Property;

	const propertySettings = propertyDetails?.hotelise?.propertySettings;
	const welcomePackId = propertySettings?.welcomePackId;

	useEffect(() => {
		const fetchWelcomePack = async () => {
			if (!welcomePackId) {
				setWelcomePackProduct(null);
				return;
			}

			try {
				setIsLoading(true);
				const response = await ProductsClient.getProductById(welcomePackId);
				setWelcomePackProduct(response.data?.content || null);
			} catch (error) {
				console.error("Failed to fetch welcome pack:", error);
				setWelcomePackProduct(null);
			} finally {
				setIsLoading(false);
			}
		};

		fetchWelcomePack();
	}, [welcomePackId]);

	const renderWelcomePackContent = () => {
		if (isLoading) {
			return (
				<div className="space-y-3">
					{[1, 2, 3, 4].map((i) => (
						<div key={i} className="flex items-start">
							<Skeleton className="h-4 w-[140px] mr-4" />
							<Skeleton className="h-4 w-48" />
						</div>
					))}
				</div>
			);
		}

		if (!welcomePackId) {
			return <div className="text-muted-foreground text-sm italic">No welcome pack assigned to this property</div>;
		}

		if (!welcomePackProduct) {
			return <div className="text-muted-foreground text-sm italic">Welcome pack not found</div>;
		}

		return (
			<div className="space-y-3">
				<div className="flex items-start">
					<span className="font-medium text-sm text-muted-foreground min-w-[160px] mr-4">Name:</span>
					<span className="text-sm text-foreground">{welcomePackProduct.name}</span>
				</div>
				<div className="flex items-start">
					<span className="font-medium text-sm text-muted-foreground min-w-[160px] mr-4">Product Code:</span>
					<span className="text-sm text-foreground">{welcomePackProduct.productCode}</span>
				</div>
				<div className="flex items-start">
					<span className="font-medium text-sm text-muted-foreground min-w-[160px] mr-4">Supplier:</span>
					<span className="text-sm text-foreground">{welcomePackProduct.supplier}</span>
				</div>
				<div className="flex items-start">
					<span className="font-medium text-sm text-muted-foreground min-w-[160px] mr-4">Price:</span>
					<span className="text-sm text-foreground">
						{welcomePackProduct.price} {welcomePackProduct.currency}
					</span>
				</div>
				{welcomePackProduct.description && (
					<div className="flex items-start">
						<span className="font-medium text-sm text-muted-foreground min-w-[160px] mr-4">Description:</span>
						<span className="text-sm text-foreground">{welcomePackProduct.description}</span>
					</div>
				)}
				{welcomePackProduct.subProducts &&
					welcomePackProduct.subProducts.length > 0 && (
						<div className="flex items-start">
							<span className="font-medium text-sm text-muted-foreground min-w-[160px] mr-4">Items:</span>
							<div className="flex flex-col gap-2">
								{welcomePackProduct.subProducts.map((subProduct) => (
									<div key={subProduct.id} className="p-3 bg-background rounded-md border border-border">
										<span className="text-sm text-foreground font-medium">{subProduct.name}</span>
										<div className="flex gap-4 mt-1.5 text-xs text-muted-foreground">
											<span>Code: {subProduct.productCode}</span>
											<span>Qty: {subProduct.quantity}</span>
											<span>
												{subProduct.price} {subProduct.currency}
											</span>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
			</div>
		);
	};

	return (
		<div className="space-y-6">
			<section>
				<Card className="p-6">
					<div className="space-y-1 mb-4">
						<h2 className="text-lg font-medium tracking-tight">Property Settings</h2>
						<p className="text-sm text-muted-foreground">
							Property configuration overview.
						</p>
					</div>

					<div className="space-y-3">
						{propertySettings?.reportingCurrency && (
							<div className="flex items-center">
								<span className="font-medium text-sm text-muted-foreground min-w-[160px]">Reporting Currency:</span>
								<Badge variant="secondary" className="px-2 py-1 font-mono">
									{propertySettings.reportingCurrency}
								</Badge>
							</div>
						)}
						{propertySettings?.invoicingCurrency && (
							<div className="flex items-center">
								<span className="font-medium text-sm text-muted-foreground min-w-[160px]">Invoicing Currency:</span>
								<Badge variant="secondary" className="px-2 py-1 font-mono">
									{propertySettings.invoicingCurrency}
								</Badge>
							</div>
						)}
						{propertySettings?.isDisabled !== undefined && (
							<div className="flex items-center">
								<span className="font-medium text-sm text-muted-foreground min-w-[160px]">Status:</span>
								<Badge variant={propertySettings.isDisabled ? 'outline' : 'secondary'}>
									{propertySettings.isDisabled ? 'Disabled' : 'Active'}
								</Badge>
							</div>
						)}
					</div>
				</Card>
			</section>

			{propertySettings?.cleaningFees && propertySettings.cleaningFees.length > 0 && (
				<section>
					<Card className="p-6">
						<div className="space-y-1 mb-4">
							<h2 className="text-lg font-medium tracking-tight">Cleaning Fees</h2>
							<p className="text-sm text-muted-foreground">Fee per currency.</p>
						</div>

						<div className="space-y-3">
							{propertySettings.cleaningFees.map((fee: CurrencyAmount) => (
								<div key={fee.id || fee.currency} className="flex items-center gap-3">
									<Badge variant="secondary" className="px-2 py-1 font-mono">
										{fee.currency}
									</Badge>
									<span className="text-sm text-foreground">{fee.value || 0}</span>
								</div>
							))}
						</div>
					</Card>
				</section>
			)}

			<section>
				<Card className="p-6">
					<div className="space-y-1 mb-4">
						<h2 className="text-lg font-medium tracking-tight">Welcome Pack</h2>
					</div>
					{renderWelcomePackContent()}
				</Card>
			</section>
		</div>
	);
};

export default PropertySettings;
